import React, { useEffect, useMemo, useState } from 'react';
import { routes } from '../../../../../utils/helpers/ApiClient';
import { AppBarCMS } from '../../../../basic';
import PhdPageContext from '../../../../common/Phd/PhdPageContext.jsx';
import { CollapseBlock, MainTitle, Spinner } from '../../../../common';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert } from 'antd';
import { useToastMessages } from '../../../../../hooks/useToastMessages.jsx';
import {
  useDeletePhdStudentMutation,
  useGetPhdStudentsQuery,
} from '../../../../../redux/services/phd';
import { usePhdReferenceData } from '../usePhdReferenceData.jsx';
import PhdStudentsTable from './PhdStudentsTable.jsx';
import PhdArchiveFilter, { usePhdArchiveStatus } from '../PhdArchiveFilter.jsx';
import { getPhdApiErrorMessage } from '../utils/phdApiFeedback.js';

const PhdStudentsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pageTitle = t('Doktorandi');
  const { archiveStatus, setArchiveStatus } = usePhdArchiveStatus(
    'phd-students-archive-status',
  );
  const [pagination, setPagination] = useState({ page: 1, per_page: 25 });

  useEffect(() => {
    document.title = `${pageTitle} - FRI CMS`;
  }, [pageTitle]);

  const {
    data: students,
    isLoading,
    error: loadError,
    refetch,
  } = useGetPhdStudentsQuery({
    archive_status: archiveStatus,
    paginated: 1,
    page: pagination.page,
    per_page: pagination.per_page,
  });
  const [deleteStudent] = useDeletePhdStudentMutation();
  const { deleteSuccess, deleteFailed } = useToastMessages();

  const {
    personMap,
    departmentMap,
    departmentCodeMap,
    studyFieldMap,
    studyFieldCodeMap,
    isLoading: isReferenceLoading,
  } = usePhdReferenceData({ includeStudents: false });

  const tableData = useMemo(() => {
    if (!students) {
      return [];
    }
    return (students?.data || []).map((student) => ({
      ...student,
      student_display_name:
        student.person?.full_name ||
        personMap.get(student.person_id) ||
        t('Neznámy používateľ'),
      department_label:
        student.department?.code ||
        departmentCodeMap.get(student.department_id) ||
        student.department?.name ||
        departmentMap.get(student.department_id),
      department_full_label:
        student.department?.name ||
        departmentMap.get(student.department_id) ||
        student.department?.code ||
        departmentCodeMap.get(student.department_id),
      study_program_label:
        student.studyField?.code ||
        studyFieldCodeMap.get(student.phd_study_field_id) ||
        undefined,
      study_program_full_label:
        studyFieldMap.get(student.phd_study_field_id) ||
        (student.studyField?.focus
          ? `${student.studyField?.study_field} - ${student.studyField.focus}`
          : student.studyField?.study_field) ||
        undefined,
    }));
  }, [
    students,
    personMap,
    departmentMap,
    departmentCodeMap,
    studyFieldMap,
    studyFieldCodeMap,
    t,
  ]);

  const handleDelete = async (row) => {
    try {
      await deleteStudent(row.id).unwrap();
      deleteSuccess();
      if (pagination.page > 1 && students?.data?.length === 1) {
        setPagination((current) => ({ ...current, page: current.page - 1 }));
      } else {
        refetch();
      }
    } catch (error) {
      deleteFailed(error);
    }
  };

  const handleArchiveStatusChange = (value) => {
    setPagination((current) => ({ ...current, page: 1 }));
    setArchiveStatus(value);
  };

  const handlePaginationChange = (nextPagination) => {
    setPagination({
      page: nextPagination.current,
      per_page: nextPagination.pageSize,
    });
  };

  if (isLoading || isReferenceLoading) {
    return <Spinner />;
  }

  if (loadError) {
    return (
      <Alert
        type="error"
        showIcon
        title={getPhdApiErrorMessage(
          loadError,
          t(
            'PhD údaje sa nepodarilo načítať. Obnovte stránku alebo skúste znova.',
          ),
        )}
      />
    );
  }

  return (
    <>
      <AppBarCMS
        title={pageTitle}
        backUrl={routes.cmsDashboard}
        backUrlTooltip={t('Nástenka')}
        createText={t('Pridať doktoranda')}
        createUrl={routes.cmsPhdStudentsCreate}
        permissionsBasic="phd-students"
      />
      <div style={{ margin: '10px' }}>
        <PhdPageContext
          title={t('Ako funguje evidencia doktorandov?')}
          description={t(
            'Administrátor doktorandského štúdia tu vytvára a spravuje študijné profily. Po doplnení povinných údajov môže doktoranda aktivovať a pokračovať témou a individuálnym plánom.',
          )}
        />
        <PhdArchiveFilter
          archiveStatus={archiveStatus}
          onChange={handleArchiveStatusChange}
          storageKey="phd-students-archive-status"
        />
        <CollapseBlock>
          <MainTitle title={pageTitle} />
          <PhdStudentsTable
            students={tableData}
            onDetailClick={(row) =>
              navigate(`${routes.cmsPhdStudentsEdit}/${row.id}`)
            }
            onDelete={handleDelete}
            pagination={{
              current: students?.current_page ?? pagination.page,
              pageSize: students?.per_page ?? pagination.per_page,
              total: students?.total ?? 0,
              showSizeChanger: true,
              pageSizeOptions: [10, 25, 50, 100],
            }}
            onPaginationChange={handlePaginationChange}
          />
        </CollapseBlock>
      </div>
    </>
  );
};

export default PhdStudentsPage;
